import { Form, type FormInstance, Table } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { ProdutoComposicaoBody } from '@/features/produto/types/ProdutoComposicao';
import { InputNumber } from '@/components/input/InputNumber';
import { numberToCurrency, numberToPercent } from '@/std/format';
import { roundAbnt, roundAbntObject } from '@/std/math';

function calcularTotal(produtoComposicao: ProdutoComposicaoBody[]): {
    percentual: number;
    quantidade: number;
    venda: number;
} {
    const total = {
        percentual: 0,
        quantidade: 0,
        venda: 0,
    };

    for (const item of produtoComposicao) {
        if (item.excluir === 'S') {
            continue;
        }

        const quantidade = item.prc_quantidade ?? 0;
        total.quantidade += quantidade;
        total.venda += quantidade * (item.produto?.pro_preco_venda ?? 0);
        total.percentual += item.prc_rateio_percentual ?? 0;
    }

    roundAbntObject(total, 2);
    return total;
}

export function ProdutoTabComposicaoKitSummary({
    form,
    produtoComposicao,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    produtoComposicao: ProdutoComposicaoBody[];
    setFormDirty: (dirty: boolean) => void;
}) {
    const total = calcularTotal(produtoComposicao);

    const precoCusto = Form.useWatch('pro_preco_custo', form) ?? 0;
    const precoVenda = Form.useWatch('pro_preco_venda', form) ?? 0;

    const recalcularTotaisProdutos = (): void => {
        const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;

        const produtoComposicaoForm: ProdutoComposicaoBody[] =
            form.getFieldValue('produto_composicao') || [];

        const updatedComposicao = produtoComposicaoForm.map((item) => {
            if (item.excluir === 'S') {
                return item;
            }

            const totalItemKit = roundAbnt(
                ((item.prc_rateio_percentual ?? 0) / 100) * precoVenda,
                4,
            );

            return {
                ...item,
                totalItemKit,
            };
        });

        form.setFieldValue('produto_composicao', updatedComposicao);
        setFormDirty(true);
    };

    return (
        <Table.Summary.Row className='font-bold'>
            <Table.Summary.Cell colSpan={3} index={0} />

            <Table.Summary.Cell align='center' index={1}>
                Total
            </Table.Summary.Cell>

            <Table.Summary.Cell align='center' index={2}>
                {numberToCurrency(precoCusto)}
            </Table.Summary.Cell>

            <Table.Summary.Cell align='center' index={3}>
                {numberToCurrency(total.venda)}
            </Table.Summary.Cell>

            <Table.Summary.Cell align='center' index={4}>
                {total.quantidade}
            </Table.Summary.Cell>

            <Table.Summary.Cell
                align='center'
                className={total.percentual !== 100 ? 'text-red-500' : ''}
                index={5}
            >
                {numberToPercent(total.percentual)}
            </Table.Summary.Cell>

            <Table.Summary.Cell align='center' index={6}>
                <InputNumber
                    decimalScale={2}
                    onBlur={recalcularTotaisProdutos}
                    onChange={(e) => {
                        form.setFieldValue('pro_preco_venda', e);
                    }}
                    type='$'
                    value={precoVenda}
                />
            </Table.Summary.Cell>

            <Table.Summary.Cell align='center' index={7} />
        </Table.Summary.Row>
    );
}
